import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, from, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { AuthApiService } from '../services/auth-api.service';
import { localizeAuthError } from '../utils/localize-error';
import { AuthActions } from './auth.actions';
import { selectAuthDestination, selectReady, selectUserId } from './auth.selectors';

@Injectable()
export class AuthEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(AuthApiService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  private initialized = false;

  ngrxOnInitEffects(): Action {
    return AuthActions.init();
  }

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.init),
      exhaustMap(() => {
        if (this.initialized) {
          return EMPTY;
        }
        this.initialized = true;

        const passwordRecovery = this.api.detectRecoveryFromUri();

        this.api.onAuthStateChange((event, session) => {
          this.store.dispatch(
            AuthActions.sessionChanged({
              userId: session?.user.id ?? null,
              passwordRecovery: event === 'PASSWORD_RECOVERY',
              event,
            }),
          );
          if (session && event !== 'SIGNED_OUT') {
            this.store.dispatch(AuthActions.loadProfile());
          }
        });

        return from(this.api.getSession()).pipe(
          switchMap((session) => {
            const userId = session?.user.id ?? null;
            if (!userId) {
              return of(
                AuthActions.initSuccess({
                  userId: null,
                  passwordRecovery,
                  profile: null,
                }),
              );
            }
            return from(this.api.fetchCurrentProfile(userId)).pipe(
              map((profile) =>
                AuthActions.initSuccess({
                  userId,
                  passwordRecovery,
                  profile,
                }),
              ),
              catchError(() =>
                of(
                  AuthActions.initSuccess({
                    userId,
                    passwordRecovery,
                    profile: null,
                  }),
                ),
              ),
            );
          }),
          catchError(() =>
            of(
              AuthActions.initSuccess({
                userId: null,
                passwordRecovery: false,
                profile: null,
              }),
            ),
          ),
        );
      }),
    ),
  );

  loadProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadProfile),
      withLatestFrom(this.store.select(selectUserId)),
      exhaustMap(([, userId]) => {
        if (!userId) {
          return of(AuthActions.profileLoaded({ profile: null }));
        }
        return from(this.api.fetchCurrentProfile(userId)).pipe(
          map((profile) => AuthActions.profileLoaded({ profile })),
          catchError(() => of(AuthActions.profileLoaded({ profile: null }))),
        );
      }),
    ),
  );

  signInWithEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signInWithEmail),
      exhaustMap(({ email, password }) =>
        from(this.api.signInWithEmail(email, password)).pipe(
          switchMap(() =>
            from(this.api.fetchCurrentProfile()).pipe(
              switchMap((profile) =>
                of(
                  AuthActions.profileLoaded({ profile }),
                  AuthActions.authFlowSuccess(),
                ),
              ),
            ),
          ),
          catchError((error) =>
            of(
              AuthActions.signInFailed({ error: localizeAuthError(error) }),
            ),
          ),
        ),
      ),
    ),
  );

  signInWithGoogle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signInWithGoogle),
      exhaustMap(() =>
        from(this.api.signInWithGoogle()).pipe(
          // Browser navigates to Google; keep UI pending until unload.
          switchMap(() => EMPTY),
          catchError((error) =>
            of(
              AuthActions.signInFailed({ error: localizeAuthError(error) }),
            ),
          ),
        ),
      ),
    ),
  );

  signUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signUp),
      exhaustMap(({ email, password }) =>
        from(this.api.signUpWithEmail(email, password)).pipe(
          map(() => AuthActions.signUpSuccess({ email: email.trim() })),
          catchError((error) =>
            of(
              AuthActions.signUpFailed({ error: localizeAuthError(error) }),
            ),
          ),
        ),
      ),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      exhaustMap(({ email }) =>
        from(this.api.resetPasswordForEmail(email)).pipe(
          map(() => AuthActions.resetPasswordSuccess({ email: email.trim() })),
          catchError((error) =>
            of(
              AuthActions.resetPasswordFailed({
                error: localizeAuthError(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updatePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.updatePassword),
      exhaustMap(({ password }) =>
        from(this.api.updatePassword(password)).pipe(
          map(() => AuthActions.updatePasswordSuccess()),
          catchError((error) =>
            of(
              AuthActions.updatePasswordFailed({
                error: localizeAuthError(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  setUsername$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setUsername),
      exhaustMap(({ username }) =>
        from(this.api.setUsername(username)).pipe(
          map((profile) => AuthActions.setUsernameSuccess({ profile })),
          catchError((error) => {
            const code =
              error && typeof error === 'object' && 'code' in error
                ? String((error as { code: unknown }).code)
                : '';
            const message =
              code === '23505'
                ? 'To korisničko ime je već zauzeto.'
                : localizeAuthError(error);
            return of(AuthActions.setUsernameFailed({ error: message }));
          }),
        ),
      ),
    ),
  );

  signOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signOut),
      exhaustMap(() =>
        from(this.api.signOut()).pipe(
          map(() => AuthActions.signOutSuccess()),
          catchError(() => of(AuthActions.signOutSuccess())),
        ),
      ),
    ),
  );

  exchangeCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.exchangeCode),
      exhaustMap(({ code }) =>
        from(this.api.exchangeCodeForSession(code)).pipe(
          switchMap(() =>
            from(this.api.fetchCurrentProfile()).pipe(
              switchMap((profile) =>
                of(
                  AuthActions.profileLoaded({ profile }),
                  AuthActions.authFlowSuccess(),
                ),
              ),
            ),
          ),
          catchError((error) =>
            of(
              AuthActions.exchangeCodeFailed({
                error: localizeAuthError(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  navigateAfterAuth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AuthActions.authFlowSuccess,
          AuthActions.updatePasswordSuccess,
          AuthActions.setUsernameSuccess,
          AuthActions.signOutSuccess,
        ),
        withLatestFrom(
          this.store.select(selectAuthDestination),
          this.store.select(selectReady),
        ),
        filter(([, dest, ready]) => ready && dest != null),
        tap(([, dest]) => {
          void this.router.navigateByUrl(dest!);
        }),
      ),
    { dispatch: false },
  );
}
